/**
 * Amazon API – direct call from frontend.
 * User provides API Key Name and API Key in the Extract form.
 */

import firebase from "../components/firebase";
import { base } from "../components/firebase";

const JUNGLE_SCOUT_BASE = "https://developer.junglescout.com";

export interface KeywordAttribute {
  name?: string;
  organic_rank?: number | null;
  sponsored_rank?: number | null;
  overall_rank?: number | null;
  ppc_bid_exact?: number | null;
  ppc_bid_broad?: number | null;
  monthly_search_volume_exact?: number | null;
  monthly_search_volume_broad?: number | null;
  ease_of_ranking_score?: number | null;
  [key: string]: unknown;
}

export interface KeywordByAsinRow {
  id: string;
  type: string;
  attributes: KeywordAttribute;
}

export interface KeywordsByAsinResult {
  data?: KeywordByAsinRow[];
  errors?: Array<{ title: string; detail: string; code: string; status: string }>;
}

export interface BrandInfo {
  brandName: string;
  totalListings: number;
  averagePosition: number;
  averagePrice: number;
  organicListingsCount: number;
  organicAveragePosition: number;
  organicAveragePrice: number;
  sponsoredListingsCount: number;
  sponsoredAveragePosition: number;
  sponsoredAveragePrice: number;
}

export interface TopListing {
  asin: string;
  title: string;
  brand: string;
  visits: number;
  purchases: number;
  conversionRate: number;
}

export interface KeywordSearchResult {
  searchVolume30Days?: number;
  averageAdValue?: number;
  brands?: BrandInfo[];
  topListings?: TopListing[];
  errors?: Array<{ title: string; detail: string; code: string; status: string }>;
}

/**
 * Изчислява необходимите продажби за първа страница
 * @param keyword - Обект с данни за ключовата дума (API attributes)
 * @returns Необходими продажби месечно (или null ако няма search volume)
 */
export function estimateSalesToFirstPage(keyword: KeywordAttribute): number | null {
  const searchVolume = keyword.monthly_search_volume_exact ?? 0;
  const organicRank = keyword.organic_rank ?? 0;
  const ppcBidExact = keyword.ppc_bid_exact ?? 0;
  const ppcBidBroad = keyword.ppc_bid_broad ?? 0;

  if (searchVolume === 0) {
    return null;
  }

  // СТЪПКА 1: Базови продажби – 3% от search volume (30% CTR * 10% Conversion)
  const baseSales = searchVolume * 0.03;

  // СТЪПКА 2: Фактор за конкуренция (от PPC bid)
  const ppcBid = ppcBidExact > 0 ? ppcBidExact : ppcBidBroad;
  let competitionFactor: number;
  if (ppcBid === 0 || ppcBid < 0.5) {
    competitionFactor = 1.0;
  } else if (ppcBid < 1.5) {
    competitionFactor = 1.3;
  } else if (ppcBid < 3.0) {
    competitionFactor = 1.6;
  } else {
    competitionFactor = 2.0;
  }

  // СТЪПКА 3: Фактор за позиция (от Organic rank)
  let rankFactor: number;
  if (organicRank === 0 || organicRank > 50) {
    rankFactor = 0.35;
  } else if (organicRank > 20) {
    rankFactor = 0.28;
  } else if (organicRank > 10) {
    rankFactor = 0.22;
  } else {
    rankFactor = 0.12;
  }

  // СТЪПКА 4: Финално изчисление
  const salesNeeded = baseSales * competitionFactor * rankFactor;
  return Math.round(salesNeeded);
}

/** Normalize API key fields: trim and remove newlines (e.g. when pasting). */
function normalizeCredential(value: string): string {
  return value.replace(/\s+/g, "").trim();
}

/** Call Jungle Scout keywords by ASIN API from the frontend. Endpoint: keywords/keywords_by_asin_query (per Python client). */
export async function extractKeywordsByAsin(
  asin: string,
  marketplace: string,
  apiKeyName: string,
  apiKey: string
): Promise<KeywordsByAsinResult> {
  const keyName = normalizeCredential(apiKeyName);
  const key = normalizeCredential(apiKey);

  const url = `${JUNGLE_SCOUT_BASE}/api/keywords/keywords_by_asin_query?marketplace=${encodeURIComponent(marketplace)}`;
  const body = {
    data: {
      type: "keywords_by_asin_query",
      attributes: {
        asins: [asin],
        include_variants: true,
      },
    },
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/vnd.api+json",
      Accept: "application/vnd.junglescout.v1+json",
      Authorization: `${keyName}:${key}`,
      "X-API-Type": "junglescout",
    },
    body: JSON.stringify(body),
  });

  const json = (await response.json()) as KeywordsByAsinResult;

  if (!response.ok) {
    const message = json?.errors?.[0]?.detail || `Amazon API: ${response.status}`;
    throw new Error(message);
  }

  return json;
}

/** Call Jungle Scout Share of Voice API from the frontend. Endpoint: /api/share_of_voice */
export async function scanAmazonByKeyword(
  keyword: string,
  marketplace: string,
  apiKeyName: string,
  apiKey: string
): Promise<KeywordSearchResult> {
  const keyName = normalizeCredential(apiKeyName);
  const key = normalizeCredential(apiKey);

  const url = `${JUNGLE_SCOUT_BASE}/api/share_of_voice?marketplace=${encodeURIComponent(marketplace)}&keyword=${encodeURIComponent(keyword.trim())}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/vnd.junglescout.v1+json",
      Authorization: `${keyName}:${key}`,
      "X-API-Type": "junglescout",
    },
  });

  const json = await response.json();

  if (!response.ok) {
    const message = json?.errors?.[0]?.detail || json?.error?.detail || `Amazon API: ${response.status}`;
    throw new Error(message);
  }

  // Process and structure the response data
  return processShareOfVoiceResult(json);
}

/** Process and structure Share of Voice API response. */
function processShareOfVoiceResult(data: any): KeywordSearchResult {
  const attributes = data?.data?.attributes;
  
  // Debug: Log the attributes to see what fields are available
  console.log('Share of Voice API Response Attributes:', attributes);
  
  // Extract search volume (30 days) and PPC bid from response
  const searchVolume30Days = attributes?.estimated_30_day_search_volume ?? 0;
  // Extract exact_suggested_bid_median - preserve actual 0 values, but handle null/undefined
  const averageAdValue = attributes?.exact_suggested_bid_median ?? 
                        attributes?.ppc_bid ?? 
                        attributes?.average_ppc_bid ?? 
                        null;
  
  // Debug: Log the extracted values
  console.log('Extracted averageAdValue:', averageAdValue);
  console.log('exact_suggested_bid_median:', attributes?.exact_suggested_bid_median);

  // Extract brand information from share of voice data
  const brandsData = attributes?.brands || [];
  
  const brands: BrandInfo[] = brandsData.map((brandData: any) => ({
    brandName: brandData.brand || "Unknown",
    totalListings: brandData.combined_products || 0,
    averagePosition: brandData.combined_average_position || 0,
    averagePrice: brandData.combined_average_price || 0,
    organicListingsCount: brandData.organic_products || 0,
    organicAveragePosition: brandData.organic_average_position ?? 0,
    organicAveragePrice: brandData.organic_average_price ?? 0,
    sponsoredListingsCount: brandData.sponsored_products || 0,
    sponsoredAveragePosition: brandData.sponsored_average_position ?? 0,
    sponsoredAveragePrice: brandData.sponsored_average_price ?? 0,
  }));

  // Extract top 3 ASINs with conversion rates
  const topAsins = attributes?.top_asins || [];
  
  const topListings: TopListing[] = topAsins.slice(0, 3).map((asinData: any) => ({
    asin: asinData.asin || "",
    title: asinData.name || "",
    brand: asinData.brand || "",
    visits: asinData.clicks || 0,
    purchases: asinData.conversions || 0,
    conversionRate: asinData.conversion_rate ? asinData.conversion_rate * 100 : 0, // Convert decimal to percentage
  }));

  return {
    searchVolume30Days,
    averageAdValue,
    brands,
    topListings,
    errors: data.errors,
  };
}

/** Save extracted keyword data to Firebase Realtime Database for the current user. */
export function saveExtractToFirebase(
  asin: string,
  marketplace: string,
  keywords: KeywordByAsinRow[]
): Promise<unknown> {
  const user = firebase.getCurrentUser();
  if (!user?.uid) return Promise.reject(new Error("Not authenticated"));
  const ref = firebase.db.ref(
    `${base}/${user.uid}/jungleScoutExtracts`
  );
  return ref.push({
    asin,
    marketplace,
    extractedAt: new Date().toISOString(),
    keywords: keywords.map((k) => ({
      id: k.id,
      type: k.type,
      attributes: k.attributes,
    })),
  });
}

/** Load saved extracts for the current user. */
export async function getSavedExtracts(): Promise<
  Array<{ id: string; asin: string; marketplace: string; extractedAt: string; keywords: KeywordByAsinRow[] }>
> {
  const user = firebase.getCurrentUser();
  if (!user?.uid) return [];
  const snapshot = await firebase.db
    .ref(`${base}/${user.uid}/jungleScoutExtracts`)
    .once("value");
  const val = snapshot.val();
  if (!val) return [];
  return Object.entries(val).map(([id, v]) => ({
    id,
    ...(v as { asin: string; marketplace: string; extractedAt: string; keywords: KeywordByAsinRow[] }),
  }));
}

export type SavedKeywordScan = {
  id: string;
  keyword: string;
  marketplace: string;
  extractedAt: string;
  searchVolume30Days?: number;
  averageAdValue?: number;
  brands?: BrandInfo[];
  topListings?: TopListing[];
};

/** Save keyword scan (Share of Voice) result to Firebase for the current user. */
export function saveKeywordScanToFirebase(
  keyword: string,
  marketplace: string,
  result: KeywordSearchResult
): Promise<unknown> {
  const user = firebase.getCurrentUser();
  if (!user?.uid) return Promise.reject(new Error("Not authenticated"));
  const ref = firebase.db.ref(
    `${base}/${user.uid}/jungleScoutKeywordScans`
  );
  return ref.push({
    keyword: keyword.trim(),
    marketplace,
    extractedAt: new Date().toISOString(),
    searchVolume30Days: result.searchVolume30Days,
    averageAdValue: result.averageAdValue,
    brands: result.brands ?? [],
    topListings: result.topListings ?? [],
  });
}

/** Load saved keyword scans for the current user. */
export async function getSavedKeywordScans(): Promise<SavedKeywordScan[]> {
  const user = firebase.getCurrentUser();
  if (!user?.uid) return [];
  const snapshot = await firebase.db
    .ref(`${base}/${user.uid}/jungleScoutKeywordScans`)
    .once("value");
  const val = snapshot.val();
  if (!val) return [];
  return Object.entries(val).map(([id, v]) => ({
    id,
    ...(v as Omit<SavedKeywordScan, "id">),
  }));
}
